import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import { CHILDREN } from 'constants/props'

import { canOfferChunkReload, isChunkLoadError, reloadForChunkError } from './chunkLoadError'
import styles from './ErrorBoundary.scss'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
      errorInformation: null,
      isChunkLoadError: false,
    }
  }

  componentDidCatch(error, errorInformation) {
    const { onCatchError } = this.props

    this.setState({
      error,
      errorInformation,
      isChunkLoadError: isChunkLoadError(error),
    })

    if (onCatchError) {
      onCatchError(error, errorInformation)
    }
  }

  render() {
    const {
      className,
      children,
      errorMessage,
      errorTitle,
      reloadLabel,
      showReload,
      showReloadOnChunkError,
      stackErrorClassName,
      DEVELOPMENT,
    } = this.props

    const { error, errorInformation, isChunkLoadError: caughtChunkLoadError } = this.state
    const title = errorTitle || 'Something went wrong'
    const shouldShowChunkMessage = caughtChunkLoadError && errorMessage
    const shouldShowReload = (showReload || showReloadOnChunkError) && caughtChunkLoadError && canOfferChunkReload()

    if (error) {
      return (
        <div className={classnames(styles.error, className)} role="alert">
          <h2 className={styles.error__title} title={title}>
            {title}
          </h2>

          {shouldShowChunkMessage && <p className={styles.error__message}>{errorMessage}</p>}

          {shouldShowReload && (
            <button className={styles.error__reload} onClick={reloadForChunkError} type="button">
              {reloadLabel || 'Reload page'}
            </button>
          )}

          {DEVELOPMENT && (
            <>
              <h3 className={styles.error__subtitle} title="Error details">
                Error details:
              </h3>

              <p className={styles.error__details}>{error.toString().slice(7, error.toString().length)}</p>

              <h3 className={styles.error__subtitle} title="Component stack error details">
                Component stack error details:
              </h3>

              <pre className={classnames(styles['error__component-stack'], stackErrorClassName)}>
                {errorInformation.componentStack.slice(1, errorInformation.componentStack.length)}
              </pre>
            </>
          )}
        </div>
      )
    }

    return children
  }
}

ErrorBoundary.propTypes = {
  children: CHILDREN,
  className: PropTypes.string,
  DEVELOPMENT: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorTitle: PropTypes.string,
  onCatchError: PropTypes.func,
  reloadLabel: PropTypes.string,
  showReload: PropTypes.bool,
  showReloadOnChunkError: PropTypes.bool,
  stackErrorClassName: PropTypes.string,
}

ErrorBoundary.defaultProps = {
  children: null,
  className: null,
  DEVELOPMENT: true,
  errorMessage: null,
  errorTitle: null,
  onCatchError: undefined,
  reloadLabel: null,
  showReload: false,
  showReloadOnChunkError: false,
  stackErrorClassName: null,
}

export default ErrorBoundary
export { isChunkLoadError, canOfferChunkReload, reloadForChunkError } from './chunkLoadError'
